import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import { formatCurrency, formatDate, getMonthsShort } from '../utils';
import { ComposedChart, Area, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from 'recharts';
import {
  IconAlert, IconHistory, IconDoc, IconPlus, IconCalendar, IconClock,
  IconLocation, IconChevronRight, IconMoney,
} from '../components/Icons';
import Modal from '../components/Modal';
import HelpHint from '../components/HelpHint';

// ─── Plural helper ─────────────────────────────────────────────────────────
// PL: 1 / 2-4 / 5+ — "faktura / faktury / faktur"
function pluralPL(n, one, few, many) {
  const abs = Math.abs(n);
  const m10 = abs % 10;
  const m100 = abs % 100;
  if (abs === 1) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few;
  return many;
}

// Wiek zaległości w miesiącach od podstawa_naliczania
function naroslaAgeMonths(podstawa) {
  if (!podstawa) return 0;
  const d = new Date(podstawa);
  const now = new Date();
  return Math.max(0, (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth()));
}

// Gradient: świeże (<3 mies) — neutralny ink, średnie (3-12) — pomarańczowy, stare (>12) — czerwony
function naroslaColor(months) {
  if (months >= 12) return 'var(--err2)';
  if (months >= 3)  return 'var(--acc2)';
  return 'var(--ink2)';
}

// Delta % vs poprzedni okres — kolor zależny od kierunku i kontekstu (przychód: + zielone, koszt: + czerwone)
function DeltaPill({ current, previous, invertColor = false }) {
  if (previous == null || previous === 0) return null;
  const delta = ((current - previous) / Math.abs(previous)) * 100;
  if (Math.abs(delta) < 0.5) return null;
  const positive = delta > 0;
  const goodWhenPositive = !invertColor;
  const isGood = (positive && goodWhenPositive) || (!positive && !goodWhenPositive);
  const color = isGood ? 'var(--ok2)' : 'var(--err2)';
  const arrow = positive ? '▲' : '▼';
  return (
    <span style={{ fontSize: '10px', fontWeight: 700, color, fontFamily: 'monospace' }}>
      {arrow} {Math.abs(delta).toFixed(0)}%
    </span>
  );
}

// ─── KPI z trendem (opcjonalnie klikalne — drill-down breakdown) ─────────────

function KPI({ label, value, sub, color, current, previous, invertColor, onClick }) {
  const { t } = useTranslation();
  const clickable = !!onClick;
  const inner = (
    <>
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <p style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)' }}>{label}</p>
        {current != null && previous != null && (
          <DeltaPill current={current} previous={previous} invertColor={invertColor} />
        )}
      </div>
      <p style={{ fontSize: '22px', fontWeight: 800, fontFamily: 'monospace', color: color || 'var(--ink)' }}>{value}</p>
      <div className="flex items-center justify-between gap-2 mt-0.5">
        {sub && <p style={{ fontSize: '10px', color: 'var(--ink3)', minWidth: 0 }} className="truncate">{sub}</p>}
        {clickable && (
          <span style={{ color: 'var(--ink3)', flexShrink: 0, opacity: 0.6 }} title={t('dashboardV2.showDetails')}>
            <IconChevronRight className="w-3 h-3" />
          </span>
        )}
      </div>
    </>
  );

  if (!clickable) {
    return <div className="ds-card p-4">{inner}</div>;
  }
  return (
    <button
      onClick={onClick}
      className="ds-card p-4 transition-all"
      style={{ textAlign: 'left', cursor: 'pointer', width: '100%' }}
      onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(184,84,40,0.4)'}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
      {inner}
    </button>
  );
}

// ─── Onboarding helpers (reużyte logiką, lokalna kopia) ──────────────────────

function IconSuccess({ size = 36 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
      <circle cx="20" cy="20" r="18" stroke="var(--ok2)" strokeWidth="2" fill="none"/>
      <path d="M12 20l6 6 10-12" stroke="var(--ok2)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function AllDoneBanner({ onDismiss }) {
  const { t } = useTranslation();
  return (
    <div style={{
      background: 'var(--bg2)', border: '1px solid rgba(90,158,114,0.35)',
      borderLeft: '4px solid var(--ok2)', borderRadius: '10px', padding: '16px 20px',
      display: 'flex', alignItems: 'center', gap: '16px', boxShadow: 'var(--shadow-card)',
    }}>
      <IconSuccess />
      <div style={{ flex: 1 }}>
        <p style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink)', marginBottom: '2px' }}>
          {t('onboarding.allDone')}
        </p>
        <p style={{ fontSize: '11px', color: 'var(--ink3)' }}>{t('onboarding.allDoneDesc')}</p>
      </div>
      <button onClick={onDismiss}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink3)', fontSize: '18px', lineHeight: 1, padding: '4px', flexShrink: 0 }}>×</button>
    </div>
  );
}

function OnboardingChecklist({ onboarding, onNavigate, onDismiss, onSkipStep }) {
  const { t } = useTranslation();
  const skipped = onboarding.skipped_steps || [];
  // realDone = wykryte automatycznie z danych usera; manualSkip = user sam odhaczył.
  // done = realDone || manualSkip — krok wygląda jak zrobiony, ale rozróżniamy żeby pozwolić "cofnij".
  const steps = [
    { key: 'account', realDone: true, label: t('onboarding.stepAccount'), desc: t('onboarding.stepAccountDesc') },
    { key: 'klient', realDone: onboarding.has_klienci, label: t('onboarding.stepClient'), desc: t('onboarding.stepClientDesc'),
      action: () => onNavigate('klienci', null, { action: 'add' }), actionLabel: t('onboarding.addClient') },
    { key: 'spacer', realDone: onboarding.has_spacery, label: t('onboarding.stepSpace'), desc: t('onboarding.stepSpaceDesc'),
      action: () => onNavigate('spacery', null, { action: 'add' }), actionLabel: t('onboarding.addSpace') },
    { key: 'settings', realDone: onboarding.has_settings, label: t('onboarding.stepSettings'), desc: t('onboarding.stepSettingsDesc'),
      action: () => onNavigate('ustawienia'), actionLabel: t('onboarding.goSettings') },
  ].map(s => ({ ...s, manualSkip: skipped.includes(s.key), done: s.realDone || skipped.includes(s.key) }));

  const doneCount = steps.filter(s => s.done).length;
  const progress = Math.round((doneCount / steps.length) * 100);

  const toggleStep = (step) => {
    // 'account' jest zawsze done (utworzone konto) — nieklikalne. Inne realDone też nie potrzebują manual.
    if (step.key === 'account' || step.realDone) return;
    onSkipStep(step.key, !step.manualSkip);
  };

  return (
    <div className="rounded-xl p-5" style={{ background: 'var(--bg2)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-card)' }}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink)', marginBottom: '3px' }}>{t('onboarding.title')}</h2>
          <p style={{ fontSize: '11px', color: 'var(--ink3)' }}>{t('onboarding.subtitle', { done: doneCount, total: steps.length })}</p>
        </div>
        <button onClick={onDismiss}
          style={{ color: 'var(--ink3)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', lineHeight: 1, padding: '2px 4px' }}>×</button>
      </div>
      <div style={{ height: '4px', background: 'var(--bg4)', borderRadius: '2px', marginBottom: '16px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${progress}%`, background: 'var(--acc)', borderRadius: '2px', transition: 'width 0.5s ease' }} />
      </div>
      <div className="space-y-2">
        {steps.map((step, i) => {
          const clickableCircle = step.key !== 'account' && !step.realDone;
          return (
            <div key={step.key} className="flex items-center gap-3 rounded-lg px-3 py-2.5"
              style={{ background: step.done ? 'transparent' : 'var(--bg3)', border: `1px solid ${step.done ? 'transparent' : 'var(--border)'}` }}>
              <button
                type="button"
                onClick={() => toggleStep(step)}
                disabled={!clickableCircle}
                title={clickableCircle ? (step.manualSkip ? 'Cofnij — krok niezrobiony' : 'Oznacz jako zrobione') : ''}
                style={{
                  width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: step.done ? 'var(--ok2)' : 'var(--bg4)',
                  border: step.done ? 'none' : '1px solid var(--border)',
                  cursor: clickableCircle ? 'pointer' : 'default',
                  padding: 0,
                  transition: 'transform 0.15s',
                }}
                onMouseEnter={e => { if (clickableCircle) e.currentTarget.style.transform = 'scale(1.08)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}>
                {step.done
                  ? <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4l3 3 5-6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  : <span style={{ color: 'var(--ink3)', fontSize: '10px', fontWeight: 700 }}>{i + 1}</span>}
              </button>
              <div className="flex-1 min-w-0">
                <p style={{ fontSize: '11px', fontWeight: 600, color: step.done ? 'var(--ink3)' : 'var(--ink)', textDecoration: step.done ? 'line-through' : 'none' }}>{step.label}</p>
                {!step.done && <p style={{ fontSize: '10px', color: 'var(--ink3)', marginTop: '1px' }}>{step.desc}</p>}
                {step.manualSkip && <p style={{ fontSize: '10px', color: 'var(--ink3)', marginTop: '1px', fontStyle: 'italic' }}>Oznaczone ręcznie — kliknij kółko żeby cofnąć</p>}
              </div>
              {!step.done && step.action && (
                <button onClick={step.action}
                  style={{ background: 'var(--acc)', color: '#fff', border: 'none', borderRadius: '6px',
                    padding: '4px 10px', fontSize: '9px', fontWeight: 700, cursor: 'pointer',
                    letterSpacing: '0.04em', flexShrink: 0, whiteSpace: 'nowrap' }}>
                  {step.actionLabel}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Narosłe zaległości — collapsed per klient z opcją rozwijania ────────────

function NaroslaGroup({ g, onNavigate, t, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const oldestMonths = Math.max(...g.items.map(s => naroslaAgeMonths(s.podstawa_naliczania)));

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: '10px', background: 'var(--bg2)', overflow: 'hidden' }}>
      {/* Nagłówek — zawsze widoczny, klikalny żeby toggle */}
      <div
        onClick={() => setOpen(o => !o)}
        className="flex items-center justify-between gap-3 flex-wrap"
        style={{
          padding: '11px 14px',
          cursor: 'pointer',
          borderBottom: open ? '1px solid var(--border)' : 'none',
          transition: 'background 0.15s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
        onMouseLeave={e => e.currentTarget.style.background = 'var(--bg2)'}>
        <div className="flex items-center gap-2 min-w-0 flex-1 flex-wrap">
          {/* Chevron */}
          <span style={{
            display: 'inline-block',
            transition: 'transform 0.15s',
            transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
            color: 'var(--ink3)',
            flexShrink: 0,
          }}>
            <IconChevronRight className="w-3.5 h-3.5" />
          </span>
          <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--ink)' }} className="truncate">
            {g.nazwa}
          </span>
          <span style={{ fontSize: '9px', fontWeight: 600, color: 'var(--ink3)', textTransform: 'uppercase', letterSpacing: '0.06em', background: 'rgba(0,0,0,0.04)', padding: '2px 8px', borderRadius: '999px' }} className="flex-shrink-0">
            {g.items.length} {t('dashboardV2.spaceWordOnly', { count: g.items.length })}
          </span>
          {oldestMonths >= 12 && (
            <span style={{ fontSize: '9px', fontWeight: 700, color: 'var(--err2)', textTransform: 'uppercase', letterSpacing: '0.05em' }} className="flex-shrink-0">
              · {oldestMonths} mies.
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 ml-auto">
          <span style={{ fontFamily: 'monospace', fontWeight: 800, color: naroslaColor(oldestMonths), fontSize: '13px' }} className="whitespace-nowrap">
            {formatCurrency(g.total)}
          </span>
          {g.id && (
            <button
              onClick={e => { e.stopPropagation(); onNavigate('klient', g.id, { action: 'add-faktura' }); }}
              className="inline-flex items-center gap-1 rounded-btn"
              style={{ background: 'var(--acc)', color: '#fff', border: 'none', fontSize: '9px', fontWeight: 700, letterSpacing: '0.04em', padding: '5px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}
              title={t('dashboardV2.invoiceAllForClient')}>
              <IconPlus className="w-3 h-3" /> {t('dashboardV2.invoiceShort')}
            </button>
          )}
        </div>
      </div>

      {/* Rozwinięcie — szczegóły spacerów */}
      {open && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" style={{ background: 'var(--border)', gap: '1px' }}>
          {g.items.map(s => {
            const months = naroslaAgeMonths(s.podstawa_naliczania);
            return (
              <div key={s.id} className="flex items-start justify-between gap-2"
                style={{ background: 'var(--bg2)', padding: '9px 12px', minHeight: '50px' }}>
                <div className="min-w-0 flex-1">
                  <button onClick={() => onNavigate('spacer', s.id)}
                    style={{ fontSize: '10.5px', color: 'var(--ink)', fontWeight: 600, textAlign: 'left', lineHeight: '1.3', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                    className="hover:underline block truncate w-full">
                    {s.nazwa}
                  </button>
                  <div style={{ fontSize: '9.5px', color: months >= 12 ? 'var(--err2)' : 'var(--ink3)', marginTop: '2px', fontWeight: months >= 12 ? 600 : 400 }}>
                    {t('dashboard.fromDate', { date: formatDate(s.podstawa_naliczania) })}
                    {months >= 12 && ` · ${months} mies.`}
                  </div>
                </div>
                <span style={{ fontFamily: 'monospace', fontWeight: 700, color: naroslaColor(months), fontSize: '10.5px' }} className="flex-shrink-0 whitespace-nowrap">
                  {formatCurrency(s.kwota_narosla)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function NaroslaCard({ items, onNavigate }) {
  const { t } = useTranslation();
  const [allOpen, setAllOpen] = useState(false);
  if (!items?.length) return null;

  const groups = {};
  items.forEach(s => {
    const key = s.klient_id ?? `__${s.klient_nazwa || ''}`;
    if (!groups[key]) groups[key] = { id: s.klient_id, nazwa: s.klient_nazwa || '—', items: [], total: 0 };
    groups[key].items.push(s);
    groups[key].total += s.kwota_narosla || 0;
  });
  const groupList = Object.values(groups).sort((a, b) => b.total - a.total);
  const grandTotal = items.reduce((acc, s) => acc + (s.kwota_narosla || 0), 0);

  return (
    <div className="ds-card p-5">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <h2 style={{ fontSize: '11px', fontWeight: 700, color: 'var(--ink)' }} className="flex items-center gap-2">
          <IconHistory className="w-4 h-4" style={{ color: 'var(--acc)' }} />
          {t('dashboard.accruedDebts')} ({items.length})
          <HelpHint text={t('help.accruedDebts')} label={t('dashboard.accruedDebts')} />
        </h2>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => setAllOpen(o => !o)}
            className="hover:underline"
            style={{ fontSize: '9px', color: 'var(--ink3)', background: 'none', border: 'none', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
            {allOpen ? t('dashboardV2.collapseAll') : t('dashboardV2.expandAll')}
          </button>
          <div className="flex items-baseline gap-2">
            <span style={{ fontSize: '9px', fontWeight: 600, color: 'var(--ink3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {t('dashboard.totalLabel') || 'Razem'}
            </span>
            <span style={{ fontFamily: 'monospace', fontWeight: 800, color: 'var(--acc)', fontSize: '16px' }}>
              {formatCurrency(grandTotal)}
            </span>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {groupList.map((g, gi) => (
          <NaroslaGroup
            key={(g.id ?? gi) + '-' + (allOpen ? 'o' : 'c')}
            g={g}
            onNavigate={onNavigate}
            t={t}
            defaultOpen={allOpen}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Niezapłacone faktury — kompakt, bez sekcji "Inne", auto-hide pustych ────

function UnpaidCompact({ hosting, pakiety, dzis, onNavigate }) {
  const { t } = useTranslation();
  const totalCount = (hosting?.length || 0) + (pakiety?.length || 0);
  if (totalCount === 0) return null;

  const total = (hosting || []).reduce((s, f) => s + (Number(f.kwota) || 0), 0)
              + (pakiety || []).reduce((s, p) => s + (Number(p.kwota) || 0), 0);

  const Row = ({ kto, termin, kwota, przeterm, onClick }) => (
    <button onClick={onClick} disabled={!onClick}
      className="text-left w-full"
      style={{
        background: 'var(--bg2)',
        border: '1px solid ' + (przeterm ? 'rgba(176,88,72,0.35)' : 'var(--border)'),
        borderLeft: przeterm ? '3px solid var(--err2)' : '1px solid var(--border)',
        borderRadius: '7px',
        padding: '8px 10px',
        cursor: onClick ? 'pointer' : 'default',
      }}>
      <div className="flex items-center justify-between gap-2">
        <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--ink)' }} className="truncate">{kto || '—'}</span>
        <span style={{ fontFamily: 'monospace', fontSize: '11px', fontWeight: 700, color: przeterm ? 'var(--err2)' : 'var(--acc2)' }} className="whitespace-nowrap">
          {formatCurrency(kwota)}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2 mt-0.5">
        <span style={{ fontSize: '10px', color: przeterm ? 'var(--err2)' : 'var(--ink3)' }}>
          {termin ? t('dashboard.dueDate', { date: formatDate(termin) }) : '—'}
        </span>
        {przeterm && (
          <span style={{ fontSize: '9px', fontWeight: 700, color: 'var(--err2)' }}>
            {t('spaceDetail.overdue')}
          </span>
        )}
      </div>
    </button>
  );

  const Section = ({ title, items, kind }) => {
    if (!items?.length) return null;
    const sumaSek = items.reduce((s, x) => s + (Number(x.kwota) || 0), 0);
    return (
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span style={{ fontSize: '9px', fontWeight: 700, color: 'var(--ink3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {title} ({items.length})
          </span>
          <span style={{ fontSize: '10px', fontFamily: 'monospace', fontWeight: 700, color: 'var(--acc2)' }}>
            {formatCurrency(sumaSek)}
          </span>
        </div>
        <div className="flex flex-col gap-1.5">
          {items.map(it => {
            if (kind === 'hosting') {
              const przeterm = it.termin_platnosci && it.termin_platnosci < dzis;
              return (
                <Row key={`h${it.id}`}
                  kto={it.klient_nazwa} termin={it.termin_platnosci} kwota={it.kwota}
                  przeterm={przeterm}
                  onClick={() => onNavigate('klient', it.klient_id)} />
              );
            }
            return (
              <Row key={`p${it.id}`}
                kto={it.klient_nazwa} termin={it.data_sprzedazy} kwota={it.kwota} przeterm={false}
                onClick={it.klient_id ? (() => onNavigate('klient', it.klient_id)) : undefined} />
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="ds-card p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 style={{ fontSize: '11px', fontWeight: 700, color: 'var(--ink)' }} className="flex items-center gap-2">
          <IconDoc className="w-4 h-4" style={{ color: 'var(--acc2)' }} />
          {t('dashboard.unpaidInvoices')} ({totalCount})
        </h2>
        <span style={{ fontSize: '10px', color: 'var(--ink3)' }}>
          {t('dashboard.unpaidTotal')} <span style={{ color: 'var(--acc2)', fontFamily: 'monospace', fontWeight: 700 }}>{formatCurrency(total)}</span>
        </span>
      </div>
      <div className="flex flex-col gap-4">
        <Section title={t('dashboard.unpaidSectionHosting')} items={hosting} kind="hosting" />
        <Section title={t('dashboard.unpaidSectionSpaces')} items={pakiety} kind="pakiet" />
      </div>
    </div>
  );
}

// ─── Kończący się hosting — wzmocniony banner z akcją per wpis ───────────────

function HostingExpiringCard({ items, onNavigate }) {
  const { t } = useTranslation();
  if (!items?.length) return null;
  return (
    <div className="rounded-xl p-5" style={{
      background: 'rgba(184,84,40,0.10)',
      border: '1px solid rgba(184,84,40,0.4)',
      borderLeft: '3px solid var(--acc)',
    }}>
      <h2 style={{ color: 'var(--ink)', fontWeight: 700, fontSize: '11px' }} className="mb-3 flex items-center gap-2">
        <IconAlert className="w-4 h-4" style={{ color: 'var(--acc)' }} />
        {t('dashboard.hostingExpiring')} ({items.length})
      </h2>
      <div className="space-y-2">
        {items.map(s => (
          <div key={s.id} className="flex items-center justify-between gap-2 flex-wrap">
            <div className="min-w-0 flex-1">
              <button onClick={() => onNavigate('spacer', s.id)}
                style={{ fontSize: '11px', fontWeight: 600, color: 'var(--ink)' }} className="hover:underline">
                {s.nazwa}
              </button>
              <span style={{ fontSize: '10px', color: 'var(--ink3)' }} className="ml-2">({s.klient_nazwa})</span>
              <span style={{ fontSize: '10px', color: 'var(--ink3)' }} className="ml-2">
                · {t('dashboard.paidUntilDate', { date: formatDate(s.podstawa_naliczania) })}
              </span>
            </div>
            {s.klient_id && (
              <button
                onClick={() => onNavigate('klient', s.klient_id, { action: 'add-faktura' })}
                className="inline-flex items-center gap-1 rounded-btn"
                style={{ background: 'var(--acc)', color: '#fff', border: 'none', fontSize: '9px', fontWeight: 700, letterSpacing: '0.04em', padding: '5px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                <IconPlus className="w-3 h-3" /> {t('dashboardV2.invoiceShort')}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Faktury miesiąca jako progress bar ──────────────────────────────────────

function FakturyMiesiacBar({ rok, miesiac, onNavigate }) {
  const { t } = useTranslation();
  const [s, setS] = useState(null);
  const skip = !miesiac;
  useEffect(() => {
    if (skip) { setS(null); return; }
    api.getFakturyPodsumowanie(rok, miesiac).then(setS).catch(() => setS(null));
  }, [rok, miesiac, skip]);
  if (skip || !s) return null;

  const wystawione = s.wystawione.kwota || 0;
  if (wystawione === 0) return null;

  const oplacone = s.oplacone.kwota || 0;
  const nieoplacone = s.nieoplacone.kwota || 0;
  const przeterm = s.przeterminowane.kwota || 0;

  const paidPct = wystawione > 0 ? (oplacone / wystawione) * 100 : 0;
  const overduePct = wystawione > 0 ? (przeterm / wystawione) * 100 : 0;
  const unpaidNotOverduePct = Math.max(0, 100 - paidPct - overduePct);

  return (
    <button
      onClick={() => onNavigate && onNavigate('faktury')}
      className="ds-card p-4 w-full"
      style={{ textAlign: 'left', cursor: 'pointer' }}>
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <p style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)' }}>
          {t('invoices.summary')}
        </p>
        <span style={{ fontSize: '13px', fontWeight: 800, fontFamily: 'monospace', color: 'var(--ink)' }}>
          {formatCurrency(wystawione)}
        </span>
      </div>
      {/* Progress bar */}
      <div style={{ display: 'flex', height: '8px', borderRadius: '4px', overflow: 'hidden', background: 'var(--bg4)' }}>
        {paidPct > 0 && <div style={{ width: `${paidPct}%`, background: 'var(--ok2)', transition: 'width 0.3s' }} />}
        {unpaidNotOverduePct > 0 && <div style={{ width: `${unpaidNotOverduePct}%`, background: 'var(--acc2)' }} />}
        {overduePct > 0 && <div style={{ width: `${overduePct}%`, background: 'var(--err2)' }} />}
      </div>
      {/* Legenda */}
      <div className="flex items-center gap-4 mt-2 flex-wrap" style={{ fontSize: '10px', color: 'var(--ink3)' }}>
        {oplacone > 0 && (
          <span><span style={{ color: 'var(--ok2)', fontFamily: 'monospace', fontWeight: 700 }}>{formatCurrency(oplacone)}</span> {t('dashboardV2.paid').toLowerCase()} ({s.oplacone.szt})</span>
        )}
        {nieoplacone > 0 && (
          <span><span style={{ color: 'var(--acc2)', fontFamily: 'monospace', fontWeight: 700 }}>{formatCurrency(nieoplacone)}</span> {t('dashboardV2.toPay').toLowerCase()} ({s.nieoplacone.szt})</span>
        )}
        {przeterm > 0 && (
          <span><span style={{ color: 'var(--err2)', fontFamily: 'monospace', fontWeight: 700 }}>{formatCurrency(przeterm)}</span> {t('dashboardV2.overdue').toLowerCase()} ({s.przeterminowane.szt})</span>
        )}
      </div>
    </button>
  );
}

// ─── Abonament — czysto informacyjna karta (display-only) ────────────────────

function AbonamentCompact({ data }) {
  const { t } = useTranslation();
  const koszt = data.koszt_matterport || 0;
  const aktywne = data.liczba_aktywnych_spacerow || 0;
  const perSpacer = aktywne > 0 ? koszt / aktywne : 0;

  return (
    <div
      className="rounded-xl px-4 py-3 flex items-center justify-between flex-wrap gap-3"
      style={{ background: 'var(--bg2)', border: '1px solid var(--border)' }}>
      <div className="flex items-center gap-3">
        <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: 'rgba(184,84,40,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <IconMoney className="w-4 h-4" style={{ color: 'var(--acc2)' }} />
        </div>
        <div>
          <p style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)' }}>
            {t('dashboardV2.subscription')}
          </p>
          <p style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink)', marginTop: '1px' }}>
            <span style={{ fontFamily: 'monospace' }}>{formatCurrency(koszt)}</span>
            <span style={{ fontSize: '10px', fontWeight: 400, color: 'var(--ink3)', marginLeft: '4px' }}>/ {t('common.month')}</span>
          </p>
        </div>
      </div>
      <div className="flex items-center gap-4 flex-wrap">
        <div style={{ textAlign: 'right' }}>
          <p style={{ fontSize: '9px', color: 'var(--ink3)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.06em' }}>{t('dashboard.activeSpaces')}</p>
          <p style={{ fontSize: '12px', fontFamily: 'monospace', color: 'var(--ink)', fontWeight: 700 }}>{aktywne}</p>
        </div>
        {perSpacer > 0 && (
          <>
            <div className="hidden sm:block" style={{ width: '1px', height: '28px', background: 'var(--border)' }} />
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: '9px', color: 'var(--ink3)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.06em' }}>{t('dashboard.costPerSpace')}</p>
              <p style={{ fontSize: '12px', fontFamily: 'monospace', color: 'var(--ink2)', fontWeight: 700 }}>{formatCurrency(perSpacer)}</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Nadchodzące zdarzenia (auto-hide gdy puste) ──────────────────────────────

const TYPY_KOLOR = {
  sesja: '#b85428', spotkanie: '#5a9e72', serwis: '#7a6e9e',
  wyjazd: '#4a9eba', inne: '#a0a0aa', spacer: '#b85428',
};

function NadchodzaceZdarzenia({ zdarzenia, onNavigate }) {
  const { t, i18n } = useTranslation();
  if (!zdarzenia?.length) return null; // auto-hide

  const dzisStr = new Date().toISOString().slice(0, 10);
  const formatDzien = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const diff = Math.round((d - today) / 86400000);
    if (diff === 0) return t('dashboard.today');
    if (diff === 1) return t('dashboard.tomorrow');
    const lang = i18n.language || 'pl';
    return d.toLocaleDateString(lang === 'pl' ? 'pl-PL' : 'en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  return (
    <div className="ds-card p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 style={{ fontSize: '11px', fontWeight: 700, color: 'var(--ink)' }} className="flex items-center gap-2">
          <IconCalendar className="w-4 h-4" style={{ color: 'var(--ink3)' }} />
          {t('dashboard.upcomingEvents')}
        </h2>
        <button onClick={() => onNavigate('kalendarz')}
          style={{ fontSize: '9px', color: 'var(--acc2)', fontWeight: 600, letterSpacing: '0.05em' }}
          className="hover:underline uppercase">
          {t('dashboard.seeAll')}
        </button>
      </div>
      <div className="space-y-1.5">
        {zdarzenia.map(z => {
          const kolor = TYPY_KOLOR[z.typ] || '#a0a0aa';
          const isToday = z.data_od === dzisStr;
          return (
            <div key={z.id} className="flex items-start gap-3 rounded-lg px-3 py-2"
              style={{ background: isToday ? `${kolor}11` : 'var(--bg2)', border: `1px solid ${isToday ? kolor + '44' : 'var(--border)'}` }}>
              <div style={{ width: '3px', borderRadius: '2px', background: kolor, alignSelf: 'stretch', flexShrink: 0, marginTop: '2px', marginBottom: '2px' }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--ink)' }} className="truncate">
                    {z.tytul || t(`calendar.types.${z.typ}`)}
                  </span>
                  <span style={{ fontSize: '10px', color: isToday ? kolor : 'var(--ink3)', fontWeight: isToday ? 700 : 400, flexShrink: 0 }}>
                    {formatDzien(z.data_od)}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  {(z.godzina_od || z.data_od?.includes('T')) && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10px', color: 'var(--ink3)' }}>
                      <IconClock className="w-3 h-3" /> {z.godzina_od ? z.godzina_od.slice(0, 5) : z.data_od.slice(11, 16)}
                    </span>
                  )}
                  {(z.klient_display || z.firma_kontakt) && (
                    <span style={{ fontSize: '10px', color: 'var(--ink3)' }} className="truncate">· {z.klient_display || z.firma_kontakt}</span>
                  )}
                  {z.adres && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10px', color: 'var(--ink3)' }} className="truncate">
                      · <IconLocation className="w-3 h-3" /> {z.adres}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Okres helper ─────────────────────────────────────────────────────────────

const ROK_OPCJE = (() => {
  const current = new Date().getFullYear();
  return [current - 1, current, current + 1];
})();

// ─── Główny DashboardV2 ───────────────────────────────────────────────────────

// ─── Modal breakdown — 4 warianty (revenue / costs / profit / unpaid) ───────

function BreakdownLine({ label, value, color, sub, indent = false }) {
  return (
    <div className="flex items-center justify-between gap-3" style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{ paddingLeft: indent ? '16px' : 0, minWidth: 0, flex: 1 }}>
        <p style={{ fontSize: '12px', color: 'var(--ink2)', fontWeight: indent ? 400 : 600 }} className="truncate">{label}</p>
        {sub && <p style={{ fontSize: '10px', color: 'var(--ink3)', marginTop: '1px' }}>{sub}</p>}
      </div>
      <span style={{ fontFamily: 'monospace', fontWeight: 700, color: color || 'var(--ink)', fontSize: '13px' }} className="whitespace-nowrap">
        {value}
      </span>
    </div>
  );
}

function BreakdownTotal({ label, value, color }) {
  return (
    <div className="flex items-center justify-between gap-3" style={{ padding: '12px 0 4px', borderTop: '2px solid var(--border)', marginTop: '4px' }}>
      <p style={{ fontSize: '11px', color: 'var(--ink)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</p>
      <span style={{ fontFamily: 'monospace', fontWeight: 800, color: color || 'var(--ink)', fontSize: '18px' }}>
        {value}
      </span>
    </div>
  );
}

function PrzychodBreakdown({ data, rok, miesiac, onNavigate, onClose }) {
  const { t } = useTranslation();
  const hosting = data.przychod_hosting || 0;
  const pakiety = data.przychod_pakiety || 0;
  const total = data.przychod || 0;

  // Lista klientów którzy zapłacili w okresie — dociąga getFakturyAll i filtruje.
  const [clients, setClients] = useState(null);
  useEffect(() => {
    api.getFakturyAll().then(all => {
      const yyyy = String(rok);
      const mm = miesiac ? String(miesiac).padStart(2, '0') : null;
      const matchOkres = (date) => {
        if (!date) return false;
        return mm ? date.startsWith(`${yyyy}-${mm}`) : date.startsWith(yyyy);
      };

      const paid = all.filter(f =>
        f.status === 'zaplacona' &&
        matchOkres(f.data_wplaty || (f.typ === 'pakiet' ? f.data_wystawienia : null))
      );

      const byClient = {};
      paid.forEach(f => {
        const key = f.klient_id != null ? `c${f.klient_id}` : `_${f.klient_nazwa || 'unknown'}`;
        if (!byClient[key]) byClient[key] = {
          id: f.klient_id, nazwa: f.klient_nazwa || '—',
          hosting: 0, pakiety: 0, total: 0, count: 0,
        };
        const kw = Number(f.kwota) || 0;
        if (f.typ === 'pakiet') byClient[key].pakiety += kw;
        else byClient[key].hosting += kw;
        byClient[key].total += kw;
        byClient[key].count += 1;
      });

      setClients(Object.values(byClient).sort((a, b) => b.total - a.total));
    }).catch(() => setClients([]));
  }, [rok, miesiac]);

  return (
    <div>
      <p style={{ fontSize: '11px', color: 'var(--ink3)', marginBottom: '12px' }}>
        {t('dashboardV2.breakdownRevenueDesc')}
      </p>

      {/* Agregaty Hosting / Pakiety / Total */}
      <BreakdownLine label={t('dashboard.unpaidSectionHosting')} value={formatCurrency(hosting)} color="var(--ok2)" />
      <BreakdownLine label={t('dashboard.unpaidSectionSpaces')} value={formatCurrency(pakiety)} color="var(--ok2)" />
      <BreakdownTotal label={t('dashboard.revenue')} value={formatCurrency(total)} color="var(--ok2)" />

      {/* Lista: kto zapłacił */}
      {clients === null ? (
        <p style={{ fontSize: '11px', color: 'var(--ink3)', marginTop: '16px' }}>{t('common.loading')}</p>
      ) : clients.length === 0 ? null : (
        <div style={{ marginTop: '20px' }}>
          <p style={{ fontSize: '9px', fontWeight: 700, color: 'var(--ink3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>
            {t('dashboardV2.whoPaid')} ({clients.length})
          </p>
          <div className="flex flex-col">
            {clients.map((c, i) => {
              const clickable = c.id != null;
              const Wrap = clickable ? 'button' : 'div';
              return (
                <Wrap
                  key={c.id ?? `_${i}`}
                  onClick={clickable ? () => { onClose(); onNavigate('klient', c.id); } : undefined}
                  className={clickable ? 'text-left w-full' : ''}
                  style={{
                    padding: '8px 0',
                    borderBottom: '1px solid var(--border)',
                    background: 'none', border: 'none',
                    cursor: clickable ? 'pointer' : 'default',
                  }}>
                  <div className="flex items-center justify-between gap-3">
                    <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--ink)' }} className="truncate">
                      {c.nazwa}
                    </span>
                    <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--ok2)', fontSize: '13px' }} className="whitespace-nowrap">
                      {formatCurrency(c.total)}
                    </span>
                  </div>
                  {(c.hosting > 0 && c.pakiety > 0) && (
                    <div style={{ fontSize: '10px', color: 'var(--ink3)', marginTop: '1px' }}>
                      {t('dashboard.unpaidSectionHosting').toLowerCase()}: <span style={{ color: 'var(--ink2)', fontFamily: 'monospace' }}>{formatCurrency(c.hosting)}</span>
                      {' · '}
                      {t('dashboard.unpaidSectionSpaces').toLowerCase()}: <span style={{ color: 'var(--ink2)', fontFamily: 'monospace' }}>{formatCurrency(c.pakiety)}</span>
                    </div>
                  )}
                  {(c.hosting === 0 && c.pakiety > 0) && (
                    <div style={{ fontSize: '10px', color: 'var(--ink3)', marginTop: '1px' }}>
                      {t('dashboard.unpaidSectionSpaces').toLowerCase()}
                    </div>
                  )}
                  {(c.hosting > 0 && c.pakiety === 0) && (
                    <div style={{ fontSize: '10px', color: 'var(--ink3)', marginTop: '1px' }}>
                      {c.count} {pluralPL(c.count, t('dashboardV2.invoice1'), t('dashboardV2.invoice2'), t('dashboardV2.invoice5'))}
                    </div>
                  )}
                </Wrap>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function KosztyBreakdown({ rok, miesiac, data }) {
  const { t } = useTranslation();
  const [koszty, setKoszty] = useState(null);
  useEffect(() => {
    api.getKosztyDodatkowe(rok, miesiac || undefined).then(setKoszty).catch(() => setKoszty([]));
  }, [rok, miesiac]);

  const mpMies = data.koszt_mp_miesiac || 0;
  const mpRoczny = data.koszt_mp_roczny_udzial || 0;
  const dodSuma = data.koszty_dod_suma || 0;
  const total = data.koszt_total || 0;

  return (
    <div>
      <p style={{ fontSize: '11px', color: 'var(--ink3)', marginBottom: '12px' }}>
        {t('dashboardV2.breakdownCostsDesc')}
      </p>
      <BreakdownLine
        label={t('dashboardV2.subscription') + ' Matterport'}
        value={formatCurrency((mpMies || 0) + (mpRoczny || 0))}
        color="var(--err2)"
      />
      {mpMies > 0 && (
        <BreakdownLine indent label={t('dashboard.monthly_label')} value={formatCurrency(mpMies)} color="var(--ink3)" />
      )}
      {mpRoczny > 0 && (
        <BreakdownLine indent label={t('dashboard.annual')} value={formatCurrency(mpRoczny)} color="var(--ink3)" />
      )}
      <BreakdownLine
        label={t('dashboard.additionalCosts')}
        value={formatCurrency(dodSuma)}
        color={dodSuma > 0 ? 'var(--err2)' : 'var(--ink3)'}
        sub={koszty?.length ? `${koszty.length} ${pluralPL(koszty.length, t('dashboardV2.invoice1'), t('dashboardV2.invoice2'), t('dashboardV2.invoice5'))}` : null}
      />
      {koszty && koszty.length > 0 && koszty.map(k => (
        <BreakdownLine
          key={k.id}
          indent
          label={k.nazwa}
          value={formatCurrency(k.kwota)}
          color="var(--ink3)"
        />
      ))}
      <BreakdownTotal label={t('dashboard.costs')} value={formatCurrency(total)} color="var(--err2)" />
    </div>
  );
}

function ZyskBreakdown({ data }) {
  const { t } = useTranslation();
  const przychod = data.przychod || 0;
  const koszt = data.koszt_total || 0;
  const zysk = data.zysk || 0;
  const marza = przychod > 0 ? (zysk / przychod) * 100 : 0;

  return (
    <div>
      <p style={{ fontSize: '11px', color: 'var(--ink3)', marginBottom: '12px' }}>
        {t('dashboardV2.breakdownProfitDesc')}
      </p>
      <BreakdownLine label={t('dashboard.revenue')} value={formatCurrency(przychod)} color="var(--ok2)" />
      <BreakdownLine label={'− ' + t('dashboard.costs')} value={formatCurrency(koszt)} color="var(--err2)" />
      <BreakdownTotal
        label={t('dashboard.profit')}
        value={formatCurrency(zysk)}
        color={zysk >= 0 ? 'var(--ink)' : 'var(--err2)'}
      />
      {przychod > 0 && (
        <p style={{ fontSize: '11px', color: 'var(--ink3)', marginTop: '12px', textAlign: 'right' }}>
          {t('dashboardV2.margin')}: <span style={{ color: marza >= 0 ? 'var(--ok2)' : 'var(--err2)', fontWeight: 700, fontFamily: 'monospace' }}>{marza.toFixed(1)}%</span>
        </p>
      )}
    </div>
  );
}

function NiezaplaconeBreakdown({ data, onNavigate, onClose }) {
  const { t } = useTranslation();
  const dzis = new Date().toISOString().slice(0, 10);
  const hosting = data.faktury_niezaplacone || [];
  const pakiety = data.pakiety_niezaplacone || [];
  const total = hosting.reduce((s, f) => s + (Number(f.kwota) || 0), 0)
              + pakiety.reduce((s, p) => s + (Number(p.kwota) || 0), 0);

  if (hosting.length === 0 && pakiety.length === 0) {
    return (
      <div className="text-center" style={{ padding: '24px 0', color: 'var(--ink3)', fontSize: '12px' }}>
        {t('dashboardV2.allPaid')}
      </div>
    );
  }

  return (
    <div>
      <p style={{ fontSize: '11px', color: 'var(--ink3)', marginBottom: '12px' }}>
        {t('dashboardV2.breakdownUnpaidDesc')}
      </p>
      {hosting.length > 0 && (
        <>
          <p style={{ fontSize: '9px', fontWeight: 700, color: 'var(--ink3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
            {t('dashboard.unpaidSectionHosting')} ({hosting.length})
          </p>
          {hosting.map(f => {
            const overdue = f.termin_platnosci && f.termin_platnosci < dzis;
            return (
              <button key={`h${f.id}`}
                onClick={() => { onClose(); onNavigate('klient', f.klient_id); }}
                className="w-full text-left"
                style={{ padding: '8px 0', borderBottom: '1px solid var(--border)', background: 'none', border: 'none' }}>
                <div className="flex items-center justify-between gap-3">
                  <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--ink)' }} className="truncate">{f.klient_nazwa}</span>
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, color: overdue ? 'var(--err2)' : 'var(--acc2)', fontSize: '12px' }}>
                    {formatCurrency(f.kwota)}
                  </span>
                </div>
                <div style={{ fontSize: '10px', color: overdue ? 'var(--err2)' : 'var(--ink3)', marginTop: '1px' }}>
                  {f.termin_platnosci ? t('dashboard.dueDate', { date: formatDate(f.termin_platnosci) }) : '—'}
                  {overdue && ` · ${t('spaceDetail.overdue')}`}
                </div>
              </button>
            );
          })}
        </>
      )}
      {pakiety.length > 0 && (
        <>
          <p style={{ fontSize: '9px', fontWeight: 700, color: 'var(--ink3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: hosting.length ? '14px' : 0, marginBottom: '6px' }}>
            {t('dashboard.unpaidSectionSpaces')} ({pakiety.length})
          </p>
          {pakiety.map(p => (
            <button key={`p${p.id}`}
              onClick={() => p.klient_id && (onClose(), onNavigate('klient', p.klient_id))}
              disabled={!p.klient_id}
              className="w-full text-left"
              style={{ padding: '8px 0', borderBottom: '1px solid var(--border)', background: 'none', border: 'none', cursor: p.klient_id ? 'pointer' : 'default' }}>
              <div className="flex items-center justify-between gap-3">
                <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--ink)' }} className="truncate">{p.klient_nazwa || '—'}</span>
                <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--acc2)', fontSize: '12px' }}>
                  {formatCurrency(p.kwota)}
                </span>
              </div>
              {p.data_sprzedazy && (
                <div style={{ fontSize: '10px', color: 'var(--ink3)', marginTop: '1px' }}>
                  {t('dashboard.dueDate', { date: formatDate(p.data_sprzedazy) })}
                </div>
              )}
            </button>
          ))}
        </>
      )}
      <BreakdownTotal label={t('dashboard.unpaidInvoices')} value={formatCurrency(total)} color="var(--err2)" />
    </div>
  );
}

function KPIDetailModal({ type, data, rok, miesiac, onClose, onNavigate }) {
  const { t } = useTranslation();
  if (!type) return null;

  const TITLES = {
    revenue: t('dashboard.revenue'),
    costs: t('dashboard.costs'),
    profit: t('dashboard.profit'),
    unpaid: t('dashboard.unpaidInvoices'),
  };

  return (
    <Modal title={TITLES[type] || ''} onClose={onClose}>
      {type === 'revenue' && <PrzychodBreakdown data={data} rok={rok} miesiac={miesiac} onNavigate={onNavigate} onClose={onClose} />}
      {type === 'costs' && <KosztyBreakdown rok={rok} miesiac={miesiac} data={data} />}
      {type === 'profit' && <ZyskBreakdown data={data} />}
      {type === 'unpaid' && <NiezaplaconeBreakdown data={data} onNavigate={onNavigate} onClose={onClose} />}
    </Modal>
  );
}

export default function DashboardV2({ onNavigate }) {
  const { t } = useTranslation();
  const MONTHS = getMonthsShort();
  const now = new Date();
  const [rok, setRok]         = useState(now.getFullYear());
  const [miesiac, setMiesiac] = useState(now.getMonth() + 1);
  const [data, setData]       = useState(null);
  const [prevData, setPrevData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  // Lokalny override — żeby modal znikał natychmiast po kliknięciu X (przed ponownym fetchem dashboardu).
  // Server-side stan w data.onboarding.dismissed (per-user); localOverride dotyczy bieżącego widoku.
  const [localDismiss, setLocalDismiss] = useState(false);
  const [kpiModal, setKpiModal] = useState(null); // 'revenue' | 'costs' | 'profit' | 'unpaid' | null

  const dismissOnboarding = () => {
    setLocalDismiss(true);
    api.onboardingDismiss().catch(() => {}); // best-effort; UI już ukryte
  };
  const dismissAllDone = dismissOnboarding;

  // Toggle ręcznego "skip" dla danego kroku — refresh dashboardu po zapisie żeby zaktualizować skipped_steps.
  const skipStep = async (step, skip) => {
    try { await api.onboardingSkipStep(step, skip); load(); } catch (e) { /* best-effort */ }
  };

  const load = useCallback(() => {
    setLoading(true); setError(null);
    api.getDashboard(rok, miesiac)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));

    // Poprzedni okres dla delta — equivalent month w prev year jeśli mies, inaczej prev year.
    const prevRok = miesiac ? rok : rok - 1;
    const prevMies = miesiac;
    api.getDashboard(prevRok, prevMies).then(setPrevData).catch(() => setPrevData(null));
  }, [rok, miesiac]);

  useEffect(() => { load(); }, [load]);

  const wykresData = data?.wykres?.map(w => ({
    name: `${MONTHS[w.miesiac - 1].slice(0, 3)} ${String(w.rok).slice(2)}`,
    [t('dashboard.revenue')]: w.przychod,
    [t('dashboard.costs')]: w.koszt,
    [t('dashboard.profit')]: w.zysk,
  })) || [];

  const dzis = new Date().toISOString().slice(0, 10);

  const sumaH = (data?.faktury_niezaplacone || []).reduce((s, f) => s + (Number(f.kwota) || 0), 0);
  const sumaP = (data?.pakiety_niezaplacone || []).reduce((s, p) => s + (Number(p.kwota) || 0), 0);
  const niezaplaconeCnt = (data?.faktury_niezaplacone?.length || 0) + (data?.pakiety_niezaplacone?.length || 0);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 style={{ fontSize: '18px', fontWeight: 800, color: 'var(--ink)' }}>{t('dashboard.title')}</h1>

        {/* Selektor okresu */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-btn overflow-hidden" style={{ border: '1px solid var(--border)' }}>
            <button type="button" onClick={() => setMiesiac(0)} className="px-3 py-1.5 transition-colors"
              style={miesiac === 0
                ? { background: 'var(--acc)', color: '#fff', fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em' }
                : { background: 'var(--bg4)', color: 'var(--ink2)', fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em' }}>
              {t('dashboard.year')}
            </button>
            <button type="button" onClick={() => { if (miesiac === 0) setMiesiac(new Date().getMonth() + 1); }}
              className="px-3 py-1.5 transition-colors"
              style={miesiac !== 0
                ? { background: 'var(--acc)', color: '#fff', fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em' }
                : { background: 'var(--bg4)', color: 'var(--ink2)', fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em' }}>
              {t('dashboard.month')}
            </button>
          </div>
          <select value={rok} onChange={e => setRok(Number(e.target.value))}
            className="ds-input" style={{ width: 'auto', background: 'var(--bg2)' }}>
            {ROK_OPCJE.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          {miesiac !== 0 && (
            <select value={miesiac} onChange={e => setMiesiac(Number(e.target.value))}
              className="ds-input" style={{ width: 'auto', background: 'var(--bg2)' }}>
              {MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
          )}
        </div>
      </div>

      {loading && <p style={{ color: 'var(--ink3)' }}>{t('common.loading')}</p>}
      {error && (
        <div className="rounded-xl p-5" style={{ background: 'rgba(176,88,72,0.08)', border: '1px solid rgba(176,88,72,0.3)' }}>
          <p style={{ fontWeight: 600, marginBottom: '4px', color: 'var(--err2)' }}>{t('common.connectionError')}</p>
          <p style={{ fontSize: '11px', color: 'var(--err2)' }}>{error}</p>
          <button onClick={load} style={{ marginTop: '8px', fontSize: '11px', color: 'var(--err2)' }} className="underline hover:opacity-80">{t('common.tryAgain')}</button>
        </div>
      )}

      {!loading && !error && data && (<>
        {/* Onboarding — flaga dismissed jest server-side (data.onboarding.dismissed),
            localDismiss to optymistyczny override żeby ukrycie było natychmiastowe. */}
        {!data.onboarding?.dismissed && !localDismiss && data.onboarding && data.onboarding.has_klienci && data.onboarding.has_spacery && data.onboarding.has_settings && (
          <AllDoneBanner onDismiss={dismissAllDone} />
        )}
        {!data.onboarding?.dismissed && !localDismiss && data.onboarding &&
          !(data.onboarding.has_klienci && data.onboarding.has_spacery && data.onboarding.has_settings) && (
          <OnboardingChecklist onboarding={data.onboarding} onNavigate={onNavigate} onDismiss={dismissOnboarding} onSkipStep={skipStep} />
        )}

        {/* ── 1. KPI — 4 kafelki z trend delta + drill-down ────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <KPI
            label={t('dashboard.revenue')}
            value={formatCurrency(data.przychod)}
            color="var(--ok2)"
            sub={miesiac ? `${MONTHS[miesiac-1]} ${rok}` : `${t('dashboard.year')} ${rok}`}
            current={data.przychod}
            previous={prevData?.przychod}
            onClick={() => setKpiModal('revenue')}
          />
          <KPI
            label={t('dashboard.costs')}
            value={formatCurrency(data.koszt_total)}
            color="var(--err2)"
            sub={t('dashboardV2.subscription') + ' ' + formatCurrency(data.koszt_matterport)}
            current={data.koszt_total}
            previous={prevData?.koszt_total}
            invertColor
            onClick={() => setKpiModal('costs')}
          />
          <KPI
            label={t('dashboard.profit')}
            value={formatCurrency(data.zysk)}
            color={data.zysk >= 0 ? 'var(--ink)' : 'var(--err2)'}
            sub={t('dashboard.profitRevenueMinus')}
            current={data.zysk}
            previous={prevData?.zysk}
            onClick={() => setKpiModal('profit')}
          />
          <KPI
            label={t('dashboard.unpaidInvoices')}
            value={formatCurrency(sumaH + sumaP)}
            color="var(--err2)"
            sub={`${niezaplaconeCnt} ${pluralPL(niezaplaconeCnt, t('dashboardV2.invoice1'), t('dashboardV2.invoice2'), t('dashboardV2.invoice5'))}`}
            onClick={niezaplaconeCnt > 0 ? () => setKpiModal('unpaid') : undefined}
          />
        </div>

        {/* Modal breakdown — renderowany na zewnątrz wszystkich sekcji */}
        <KPIDetailModal
          type={kpiModal}
          data={data}
          rok={rok}
          miesiac={miesiac}
          onClose={() => setKpiModal(null)}
          onNavigate={onNavigate}
        />

        {/* ── 2. Faktury miesiąca progress bar + Abonament ─────────────────── */}
        <FakturyMiesiacBar rok={rok} miesiac={miesiac} onNavigate={onNavigate} />
        {data.koszt_matterport > 0 && data.liczba_aktywnych_spacerow > 0 && (
          <AbonamentCompact data={data} />
        )}

        {/* ── 3. AKCJE: priorytet — co dziś zrobić ─────────────────────────── */}
        <NaroslaCard items={data.do_rozliczenia || []} onNavigate={onNavigate} />
        <UnpaidCompact
          hosting={data.faktury_niezaplacone || []}
          pakiety={data.pakiety_niezaplacone || []}
          dzis={dzis}
          onNavigate={onNavigate}
        />
        <HostingExpiringCard items={data.konczy_hosting || []} onNavigate={onNavigate} />

        {/* ── 4. TREND: wykres 12 miesięcy ─────────────────────────────────── */}
        <div className="ds-card p-5">
          <div className="flex items-center justify-between mb-5">
            <h2 style={{ fontSize: '11px', fontWeight: 700, color: 'var(--ink)' }} className="flex items-center gap-2">
              {t('dashboard.last12months')}
              <HelpHint text={t('help.dashboardChart')} label={t('dashboard.last12months')} />
            </h2>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={wykresData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="spRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor="var(--ok2)" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="var(--ok2)" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="spProfit" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor="var(--acc)" stopOpacity={0.30} />
                  <stop offset="100%" stopColor="var(--acc)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 5" stroke="var(--border)" vertical={false} />
              <XAxis
                dataKey="name"
                tick={{ fill: 'var(--ink3)', fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                dy={8}
              />
              <YAxis
                tick={{ fill: 'var(--ink3)', fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                width={56}
                tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`}
              />
              <Tooltip
                contentStyle={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border)',
                  borderRadius: '10px',
                  fontSize: '11px',
                  boxShadow: 'var(--shadow-modal)',
                  padding: '10px 12px',
                }}
                labelStyle={{ color: 'var(--ink)', fontWeight: 700, marginBottom: '6px', fontSize: '11px' }}
                itemStyle={{ color: 'var(--ink2)', padding: '2px 0' }}
                formatter={v => [formatCurrency(v)]}
                cursor={{ stroke: 'var(--border)', strokeWidth: 1, strokeDasharray: '3 5' }}
              />
              <Legend
                wrapperStyle={{ fontSize: 10, paddingTop: '14px' }}
                iconType="circle"
                iconSize={8}
                formatter={v => <span style={{ color: 'var(--ink3)', marginLeft: '4px', marginRight: '8px' }}>{v}</span>}
              />
              {/* Koszty jako delikatny bar w tle */}
              <Bar
                dataKey={t('dashboard.costs')}
                fill="var(--err2)"
                fillOpacity={0.18}
                radius={[3, 3, 0, 0]}
                maxBarSize={18}
              />
              {/* Przychód jako gradient area */}
              <Area
                type="monotone"
                dataKey={t('dashboard.revenue')}
                stroke="var(--ok2)"
                strokeWidth={2.5}
                fill="url(#spRevenue)"
                dot={{ fill: 'var(--ok2)', r: 0 }}
                activeDot={{ r: 5, strokeWidth: 2, stroke: 'var(--bg-card)' }}
              />
              {/* Zysk jako linia z gradientem pod */}
              <Area
                type="monotone"
                dataKey={t('dashboard.profit')}
                stroke="var(--acc)"
                strokeWidth={2}
                fill="url(#spProfit)"
                dot={{ fill: 'var(--acc)', r: 0 }}
                activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--bg-card)' }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* ── 5. KALENDARZ: nadchodzące zdarzenia (auto-hide gdy puste) ────── */}
        <NadchodzaceZdarzenia zdarzenia={data.nadchodzace_zdarzenia} onNavigate={onNavigate} />
      </>)}
    </div>
  );
}
